import React, { ReactNode } from 'react';
import { ICitationRuleConfiguration } from 'common/services/configuration-service/configuration.service.types';
import { CitationStatus } from 'features/eiq-manage/enums/citation-status.enum';

const PRINT_CLEANUP_TIMEOUT = 60_000;

export const getLegalNotes = (rules: ICitationRuleConfiguration[], sequenceNumber?: number | null): string => {
  if (rules.length === 0 || sequenceNumber === null || sequenceNumber === undefined || sequenceNumber < 1) {
    return '';
  }

  const ruleIndex = Math.min(sequenceNumber - 1, rules.length - 1);
  return rules[ruleIndex]?.legalNotes || '';
};

export const getCitationCountText = (sequenceNumber?: number | null): string => {
  if (sequenceNumber === null || sequenceNumber === undefined || sequenceNumber <= 0) {
    return 'N/A';
  }

  const lastTwoDigits = sequenceNumber % 100;
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `${sequenceNumber}th`;
  }

  switch (sequenceNumber % 10) {
    case 1:
      return `${sequenceNumber}st`;
    case 2:
      return `${sequenceNumber}nd`;
    case 3:
      return `${sequenceNumber}rd`;
    default:
      return `${sequenceNumber}th`;
  }
};

const getPayNowLink = (isAutoIssueInvoice?: boolean | null, paymentLinkUrl?: string | null): ReactNode => {
  if (!paymentLinkUrl || !isAutoIssueInvoice) {
    return null;
  }

  return (
    <a href={paymentLinkUrl} target="_blank" rel="noopener noreferrer">
      (Pay Now)
    </a>
  );
};

export const getPenaltyText = (
  isAutoIssueInvoice?: boolean | null,
  paymentLinkUrl?: string | null,
  status?: CitationStatus | null | string,
  totalPaymentAmount?: number | null,
  suspensionDays?: number | null,
): ReactNode => {
  if (status === CitationStatus.Warning) {
    return (
      <span>
        This is a <span className="italic">warning</span>. A second citation will result in a fine.
      </span>
    );
  }

  const payNowLink = getPayNowLink(isAutoIssueInvoice, paymentLinkUrl);

  if (totalPaymentAmount && suspensionDays) {
    return (
      <span>
        ${totalPaymentAmount} fine with {suspensionDays} days transponder suspension {payNowLink}
      </span>
    );
  }

  if (totalPaymentAmount) {
    return (
      <span>
        ${totalPaymentAmount} fine {payNowLink}
      </span>
    );
  }

  if (suspensionDays) {
    return <span>{suspensionDays} days transponder suspension</span>;
  }

  return 'N/A';
};

export const printPdf = (blob: Blob): void => {
  const pdfUrl = URL.createObjectURL(blob);
  const printFrame = document.createElement('iframe');
  let cleanupTimeout: number | undefined;

  const cleanup = () => {
    if (cleanupTimeout !== undefined) {
      window.clearTimeout(cleanupTimeout);
    }
    URL.revokeObjectURL(pdfUrl);
    printFrame.remove();
  };

  Object.assign(printFrame.style, {
    position: 'fixed',
    right: '0',
    bottom: '0',
    width: '1px',
    height: '1px',
    border: '0',
    opacity: '0',
    pointerEvents: 'none',
  });

  printFrame.title = 'Print citation';
  printFrame.onload = () => {
    const printWindow = printFrame.contentWindow;

    if (!printWindow) {
      cleanup();
      return;
    }

    cleanupTimeout = window.setTimeout(cleanup, PRINT_CLEANUP_TIMEOUT);
    printWindow.addEventListener('afterprint', cleanup, { once: true });
    printWindow.focus();
    printWindow.print();
  };
  printFrame.src = pdfUrl;
  document.body.appendChild(printFrame);
};
